import type { AppProps } from "next/app";
import Router from "next/router";
import React, { useEffect, useState, useRef } from "react";
import Head from "next/head";
// Bootstrap CSS - import ONCE and FIRST
import 'bootstrap/dist/css/bootstrap.min.css';

// Your custom styles - import AFTER Bootstrap to ensure overrides work
import '../styles/globals.css'
import "../styles/scss/style.scss";
import "../components/Dashboard/modal-animations.css";
import "../components/Auth/Threads.css";
import "../styles/custom.css";
import "../styles/chat/chat.css";
import "../styles/chat/markdown.css";

// Import IntercomProvider with dynamic loading to avoid SSR issues
import dynamic from 'next/dynamic';
const IntercomProvider = dynamic(() => import('../components/Integrations/IntercomProvider'), { ssr: false });

// Temporarily disabled due to missing ProjectStorage dependency
// Import project enhancements dynamically to avoid SSR issues
// const ProjectEnhancer = dynamic(() =>
//   import('../components/Projects/ProjectEnhancer').then(mod => {
//     // Component returns null but has side effect of enhancing project functionality
//     return mod.default;
//   }),
//   { ssr: false }
// );

// Import PostHog utility
import posthog from '../utils/posthog';

import { IntegrationsProvider } from "../context/IntegrationsContext";

// // Import UserProvider for global user state management - REMOVED, context deleted
// import { UserProvider } from "../context/UserContext";

// Dynamic imports removed - causing chunk load errors
// These components are imported where needed instead
// if (typeof window !== 'undefined') {
//   import('../components/Common/AudioWaveLoader');
//   import('../components/Common/AudioWaveLoaders');
// }

// ========= Plugins CSS START =========
import "../public/css/plugins/feature.css";
import "../node_modules/sal.js/dist/sal.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "react-tooltip/dist/react-tooltip.css";
// ========= Plugins CSS END =========

import Loading from "../components/Loading/Loading";
import { AuthContextProvider } from "../context/AuthContext";

import OnboardingChecker from "../components/Auth/OnboardingChecker";
import { TourProvider } from "../context/TourContext";


import Layout from '../components/Layout/Layout';

export default function App({ Component, pageProps }: AppProps) {

  // Silence all console output in both development and production
  // Prevents sensitive URLs and data from appearing in browser console
  if (typeof window !== 'undefined') {
    const noop = () => { };
    console.log = noop;
    console.debug = noop;
    console.info = noop;
    console.warn = noop;
    console.error = noop;
  }
  const [loading, setLoading] = useState(false);
  // State to determine if we should show the support chat button
  // Support is now integrated into the left navbar

  // Capture initial pageview
  useEffect(() => {
    // Track initial pageview
    if (typeof window !== 'undefined' && posthog.__loaded) {
      posthog.capture('$pageview');
    }
  }, []);

  // Support is now integrated into the left navbar

  useEffect(() => {
    require("bootstrap/dist/js/bootstrap.bundle.min.js");

    const handleStart = (url: string) => url !== Router.asPath && setLoading(true);
    const handleComplete = (url: string) => {
      setLoading(false);

      // Track page views with PostHog when route changes
      if (typeof window !== 'undefined' && posthog.__loaded) {
        posthog.capture('$pageview');
      }
    };

    Router.events.on("routeChangeStart", handleStart);
    Router.events.on("routeChangeComplete", handleComplete);
    Router.events.on("routeChangeError", handleComplete);

    return () => {
      Router.events.off("routeChangeStart", handleStart);
      Router.events.off("routeChangeComplete", handleComplete);
      Router.events.off("routeChangeError", handleComplete);
    };
  }, []);

  return (
    <Layout>
      <Head>
        {/* Adds the favicon to all pages, reducing the need to include it in all components */}
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" sizes="192x192" href="/android-chrome-192x192.png" />
      </Head>
      {loading ? (
        <Loading />
      ) : (
        <AuthContextProvider>
          <IntegrationsProvider>
            <TourProvider>
              <OnboardingChecker>
                <IntercomProvider>
                  <Component {...pageProps} />
                </IntercomProvider>
              </OnboardingChecker>
            </TourProvider>
          </IntegrationsProvider>
        </AuthContextProvider>
      )}
    </Layout>
  );
}
